import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileDown, RefreshCw } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { financeApi } from '@/api/finance';
import { extractError } from '@/api/client';
import { toast } from '@/stores/toastStore';
import { formatFCFA } from '@/utils/currency';

/**
 * Relevé de compte d'un client ou d'un fournisseur (30/10/2026) : solde de départ, mouvements avec solde après chaque
 * ligne, solde final, factures encore ouvertes ; PDF à l'en-tête de la société. Écritures validées seulement.
 */
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const dateFr = (iso: string) => iso.split('-').reverse().join('/');
const montant = (c: string) => (BigInt(c) === 0n ? '' : formatFCFA(BigInt(c)));

export const ReleveComptePage = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const [au, setAu] = useState(aujourdhui());
  const [du, setDu] = useState(`${aujourdhui().slice(0, 4)}-01-01`);
  const [pdfEnCours, setPdfEnCours] = useState(false);
  const q = useQuery({ queryKey: ['finance', 'releve', contactId, du, au], queryFn: () => financeApi.getReleve(contactId!, du, au), enabled: Boolean(contactId) });
  const r = q.data;

  const telecharger = async () => {
    setPdfEnCours(true);
    try {
      const { blob, filename } = await financeApi.downloadRelevePdf(contactId!, du, au);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Relevé téléchargé', filename);
    } catch (e) {
      toast.error('PDF indisponible', extractError(e).message);
    } finally {
      setPdfEnCours(false);
    }
  };

  return (
    <>
      <Topbar
        title={r ? `Relevé de compte — ${r.tiers.nom}` : 'Relevé de compte'}
        subtitle={r ? (r.type === 'client' ? 'Client' : 'Fournisseur') : 'Client ou fournisseur'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Retour</button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void q.refetch()}><RefreshCw size={14} /> Rafraîchir</button>
            <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => void telecharger()} disabled={!r || pdfEnCours}>
              <FileDown size={14} /> {pdfEnCours ? 'PDF…' : 'PDF'}
            </button>
          </>
        }
      />
      <div style={{ padding: '16px 20px 40px', display: 'grid', gap: 14 }}>
        <div className="ds-table-panel" style={{ padding: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={{ fontSize: 12 }}>Du<br /><input type="date" className="ds-fi" value={du} max={au} onChange={(e) => setDu(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>Au<br /><input type="date" className="ds-fi" value={au} min={du} onChange={(e) => setAu(e.target.value)} /></label>
          {r?.brouillonsExclus ? (
            <span style={{ fontSize: 12, color: 'var(--warning)' }}>{r.brouillonsExclus} pièce(s) en cours de saisie non incluse(s)</span>
          ) : null}
        </div>

        {q.isLoading && <div className="ds-table-panel" style={{ padding: 24 }}>Chargement…</div>}
        {q.error && <div className="ds-alert-strip ds-alert-d"><div className="ds-alert-txt">{extractError(q.error).message}</div></div>}

        {r && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <div className="ds-table-panel" style={{ padding: 12 }}><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Solde au {dateFr(r.du)}</div><div style={{ fontWeight: 700 }}>{formatFCFA(BigInt(r.ouvertureCents))}</div></div>
              <div className="ds-table-panel" style={{ padding: 12 }}><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Débits de la période</div><div style={{ fontWeight: 700 }}>{formatFCFA(BigInt(r.totalDebitCents))}</div></div>
              <div className="ds-table-panel" style={{ padding: 12 }}><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Crédits de la période</div><div style={{ fontWeight: 700 }}>{formatFCFA(BigInt(r.totalCreditCents))}</div></div>
              <div className="ds-table-panel" style={{ padding: 12 }}><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Solde au {dateFr(r.au)}</div><div style={{ fontWeight: 700 }}>{formatFCFA(BigInt(r.soldeFinalCents))}</div></div>
            </div>
            <div className="ds-alert-strip ds-alert-i"><div className="ds-alert-txt"><strong>{r.phraseSolde}</strong></div></div>

            <div className="ds-table-panel" style={{ overflowX: 'auto' }}>
              <table className="ds-tbl" style={{ width: '100%' }}>
                <thead><tr>
                  <th>Date</th><th>Pièce</th><th>Nature</th><th>Référence</th><th>Libellé</th>
                  <th className="ds-am">Débit</th><th className="ds-am">Crédit</th><th className="ds-am">Solde</th>
                </tr></thead>
                <tbody>
                  <tr style={{ fontStyle: 'italic', background: 'var(--bg)' }}>
                    <td>{dateFr(r.du)}</td><td /><td>Solde de départ</td><td /><td /><td /><td /><td className="ds-am">{formatFCFA(BigInt(r.ouvertureCents))}</td>
                  </tr>
                  {r.lignes.map((l, i) => (
                    <tr key={i}>
                      <td>{dateFr(l.date)}</td><td style={{ fontSize: 11 }}>{l.numeroPiece}</td><td>{l.nature}</td><td style={{ fontSize: 11 }}>{l.reference}</td>
                      <td style={{ fontSize: 11 }}>{l.libelle}</td><td className="ds-am">{montant(l.debitCents)}</td><td className="ds-am">{montant(l.creditCents)}</td>
                      <td className="ds-am" style={{ fontWeight: 600 }}>{formatFCFA(BigInt(l.soldeCents))}</td>
                    </tr>
                  ))}
                  {r.lignes.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--tx-3)' }}>Aucun mouvement sur la période</td></tr>}
                </tbody>
              </table>
            </div>

            {r.factures.lignes.length > 0 && (
              <div className="ds-table-panel" style={{ overflowX: 'auto' }}>
                <div className="ds-table-header"><div className="ds-table-title">Détail par facture au {dateFr(r.au)}</div></div>
                <table className="ds-tbl" style={{ width: '100%' }}>
                  <thead><tr>
                    <th>Date</th><th>Référence</th><th className="ds-am">Montant TTC</th><th className="ds-am">{r.type === 'client' ? 'Encaissé' : 'Payé'}</th>
                    <th className="ds-am">Reste dû</th><th>Échéance</th><th className="ds-am">Retard</th>
                  </tr></thead>
                  <tbody>
                    {r.factures.lignes.map((o) => (
                      <tr key={o.reference + o.date}>
                        <td>{dateFr(o.date)}</td><td>{o.reference}</td><td className="ds-am">{formatFCFA(BigInt(o.ttcCents))}</td>
                        <td className="ds-am">{montant(o.imputeCents)}</td><td className="ds-am" style={{ fontWeight: 600 }}>{formatFCFA(BigInt(o.resteCents))}</td>
                        <td>{dateFr(o.echeance)}</td>
                        <td className="ds-am" style={o.joursRetard > 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
                          {BigInt(o.resteCents) === 0n ? 'soldée' : o.joursRetard > 0 ? `${o.joursRetard} j` : 'à échoir'}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={2}>Total</td><td className="ds-am">{formatFCFA(BigInt(r.factures.totalTtcCents))}</td>
                      <td className="ds-am">{formatFCFA(BigInt(r.factures.totalImputeCents))}</td><td className="ds-am">{formatFCFA(BigInt(r.factures.totalResteCents))}</td><td colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ fontSize: 11, color: 'var(--tx-3)' }}>Seules les pièces comptabilisées figurent sur ce relevé. Sauf erreur ou omission de notre part.</p>
          </>
        )}
      </div>
    </>
  );
};
